import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import usePageMeta from './usePageMeta';
import useSectionViewTracking from './useSectionViewTracking';
import { trackEvent } from '../analytics';
import { captureDoor, DOOR_PARAM } from '../doorAttribution';

// The behavior half of an acquisition door (#384, extracted for #387/#388).
//
// Five landing pages now implement the same funnel contract, and it is a
// contract rather than a convention: GA4 compares one door against another by
// requiring every door to emit the SAME event names and differ only by the
// `door` param. Five hand-written copies of that would drift — one page firing
// door_view twice, or capturing attribution on click instead of on arrival,
// silently rewrites the comparison the doors exist to produce. So it lives
// here once:
//
//  1. ATTRIBUTION ON ARRIVAL. captureDoor runs in a mount effect, not in the
//     CTA handler, because a visitor who reads the page and then signs in from
//     the header nav is just as much a door conversion as one who used a CTA.
//     Capturing only on click would under-count a door against its siblings.
//     The doors cross-link heavily, so this effect fires again on every hop
//     between them; captureDoor is first-touch (see doorAttribution.js) and the
//     door a visitor arrived through is what survives those hops.
//  2. door_view EXACTLY ONCE per mount, paired with that capture.
//  3. section_view once per section. The rule for what counts as "seen" is
//     shared with the long-form guides in useSectionViewTracking, so a door and
//     a guide are never measured differently.
//  4. THE AWAITING REF. Sign-in is a popup that never navigates, so the page
//     has to advance the visitor itself once `signedIn` flips — but only if
//     they got there through a CTA. Someone who signed in from the header while
//     reading is left where they were.
//
// Deliberately NOT in here: page copy, meta content and section ids, which are
// each page's own business. The hook takes them and owns nothing but the funnel.

// Signed-in CTAs carry the door on the URL so Studio can capture it on mount;
// signed-out CTAs capture it before the sign-in modal opens, because the popup
// flow never navigates and sessionStorage outlives it.
export const studioHrefFor = (door) => `/studio?${DOOR_PARAM}=${door}`;

export default function useDoorPage({ door, meta, sectionIds, signedIn, signIn }) {
  const navigate = useNavigate();
  // Set when a signed-out visitor uses a CTA. Only then does a successful
  // sign-in advance them into the studio.
  const awaitingSignInRef = useRef(false);
  const studioHref = studioHrefFor(door);

  usePageMeta(meta);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Funnel entry.
  useEffect(() => {
    captureDoor({ [DOOR_PARAM]: door });
    trackEvent('door_view', { door });
  }, [door]);

  useEffect(() => {
    if (signedIn && awaitingSignInRef.current) {
      awaitingSignInRef.current = false;
      navigate(studioHref);
    }
  }, [signedIn, navigate, studioHref]);

  // Every door tags its section_view rows with the door they came from, so GA4
  // can compare the same section across doors.
  const sectionViewParams = useMemo(() => ({ door }), [door]);
  const setSectionRef = useSectionViewTracking(sectionIds, sectionViewParams);

  // Handed to <DoorCta onActivate>. Fires the shared cta_click event and, for a
  // signed-out visitor, re-stashes attribution before opening the sign-in modal.
  const onCtaActivate = useCallback(
    (ctaId) => {
      trackEvent('cta_click', { cta_id: ctaId, signed_in: signedIn, door });
      if (!signedIn) {
        captureDoor({ [DOOR_PARAM]: door });
        awaitingSignInRef.current = true;
        signIn();
      }
    },
    [door, signedIn, signIn]
  );

  // nav_click for the secondary links a door carries (cross-door links, the
  // pricing link). Same event name on every door; `target` names the link and
  // `door` names the page it was clicked on, so GA4 can group cross-door
  // traffic by source without parsing hand-written target strings.
  const onNavClick = useCallback(
    (target) => () => {
      trackEvent('nav_click', { target, door });
    },
    [door]
  );

  return { studioHref, setSectionRef, onCtaActivate, onNavClick };
}
